<template>
  <div class="dashboard" :style="{ backgroundImage: `url(${designUrl}${isMobile ? 'DashboardPage_phone.png' : 'DashboardPage.png'})` }">
    <div class="sidebar-wrapper">
      <button class="hamburger" @click="sidebarOpen = !sidebarOpen">☰</button>

      <div class="sidebar" :class="{ open: sidebarOpen }">
        <div class="sidebar-header">
          <button class="close-btn" @click="sidebarOpen = false">✖</button>
          <p>{{ user.fullname }}</p>
          <p>{{ user.phone_number }}</p>
        </div>
        <ul class="sidebar-menu">

          <template v-if="user && user.user_type === 'Pet Owner'">
            <li @click="goToPage('DashboardPage')">Browse Pets</li>
            <li class="has-submenu" @click="petsOpen = !petsOpen">
              My Pets
              <span class="arrow" :class="{ 'arrow-open': petsOpen }">›</span>
            </li>
            <ul class="sub-menu" :class="{ 'sub-open': petsOpen }">
              <li @click="goToPage('YourPetsPage')">View My Pets</li>
              <li @click="goToPage('AddPetPage')">Add Pet</li>
              <li @click="goToPage('MeetingsPage')">Booking Requests</li>
            </ul>
            <li @click="goToPage('EditUserDashboard')">My Profile</li>
            <li @click="goToPage('LogoutPage')">Log Out</li>
          </template>

          <template v-else>
            <li @click="goToPage('DashboardPage')">Browse Pets</li>
            <li @click="goToPage('CalendarPage')">My Calendar</li>
            <li @click="goToPage('EditUserDashboard')">My Profile</li>
            <li @click="goToPage('LogoutPage')">Log Out</li>
          </template>

        </ul>
      </div>
    </div>

    <div class="signup-wrapper">

      <div class="welcome-banner">
        <div v-if="isMobile" class="welcome-text">
          <p><b>Welcome back!</b></p>
        </div>
      </div>

      <div class="calendar-button-wrapper">
        <button @click="goToCalendar" class="calendar-button">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <rect x="3" y="4" width="18" height="18" rx="2"></rect>
            <line x1="16" y1="2" x2="16" y2="6"></line>
            <line x1="8" y1="2" x2="8" y2="6"></line>
            <line x1="3" y1="10" x2="21" y2="10"></line>
          </svg>
        </button>
      </div>

      <div class="filters-wrapper">

          <button class="filters-toggle" @click="filtersOpen = !filtersOpen">
            <span>Filters</span>
            <svg :style="{ transform: filtersOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }"
              xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"
              fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="6 9 12 15 18 9"></polyline>
            </svg>
          </button>

          <div class="filters" :class="{ 'filters-hidden': isMobile && !filtersOpen }">
            <div class="filter-chip">
              <span class="chip-label">Breed</span>
              <select v-model="filters.breed" class="chip-select">
                <option value="">All</option>
                <option v-for="breed in breedOptions" :key="breed.id" :value="breed.name">{{ breed.name }}</option>
              </select>
            </div>

            <div class="filter-sep"></div>

            <div class="filter-chip no-arrow age-chip">
              <span class="chip-label">Age</span>
              <div class="pill-row">
                <button
                  v-for="age in ['Puppy','Adult','Senior']"
                  :key="age"
                  class="pill"
                  :class="{ 'pill-active': filters.age === age }"
                  @click="filters.age = filters.age === age ? '' : age"
                >{{ age }}</button>
              </div>
            </div>

            <div class="filter-sep"></div>

            <div class="filter-chip no-arrow">
              <span class="chip-label">Gender</span>
              <div class="pill-row">
                <button class="pill pill-blue" :class="{ 'pill-active': filters.gender === 'Male' }" @click="filters.gender = filters.gender === 'Male' ? '' : 'Male'">Male</button>
                <button class="pill pill-pink" :class="{ 'pill-active': filters.gender === 'Female' }" @click="filters.gender = filters.gender === 'Female' ? '' : 'Female'">Female</button>
              </div>
            </div>

            <div class="filter-sep"></div>

            <div class="filter-chip no-arrow distance-chip">
              <div class="distance-header">
                <span class="chip-label">Distance</span>
                <span class="distance-badge">{{ filters.distance }} km</span>
              </div>
              <input class="distance-slider" type="range" min="1" max="30" v-model="filters.distance" />
            </div>
          </div>
        </div>


      <div class="browse-pets pet-list">
        
        <div class="pet-grid">
          <div v-for="pet in filteredPets" :key="pet.id" class="pet-card">
            <div class="pet-card-img-wrap">
              <img
                :src="backendUrl + '/pet-pics/' + pet.pet_photo"
                :alt="pet.pet_name"
                class="pet-card-img"
              />
            </div>

            <div class="pet-body">
              <div class="pet-top">
                <p class="pet-name">{{ pet.pet_name }}</p>
              </div>
              <p class="pet-breed">{{ pet.breed }}</p>
              <p v-if="pet.location" class="pet-location">
                📍 {{ formatLocation (pet.location)}}
              </p>
              <p v-if="userCoords && pet.location_coords" class="pet-distance">
                {{ getDistance(pet) }} km away
              </p>

              <div class="badges">
                <span class="badge badge-age">{{ pet.age }}</span>
                <span class="badge" :class="pet.gender === 'Male' ? 'badge-male' : 'badge-female'">{{ pet.gender }}</span>
                <span class="badge badge-vax" title="Vaccinated">
                  <img src="/vax.png" class="vax-icon" alt="Vaccinated" />
                </span>
              </div>

              <button class="view-btn" @click="openProfile(pet)">View Profile</button>
            </div>
          </div>

          <div v-if="filteredPets.length === 0" class="empty-state">
            <p class="empty-icon">🐾</p>
            <p class="empty-text">No pets found nearby</p>
            <p class="empty-sub">Try adjusting your filters</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>


<script>
import axios from 'axios';
const backendUrl = process.env.VUE_APP_API_URL;

export default {
  props: {
    user: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      backendUrl,
      designUrl: '/background_designs/',
      isMobile: window.innerWidth <= 480,
      myPets: [],
      recentPets: [],
      editMode: false,
      pets: [],
      breedOptions: [],
      filters: {
        breed: '',
        age: '',
        gender: '',
        distance: 30
      },
      userCoords: null,
      meetings: [],
      userType: null,
      sidebarOpen: false,
      currentPage: 'DashboardPage',
      selectedPet: null,
      activeTab: 'upcoming',
      windowWidth: window.innerWidth,
      petsOpen: false,
      filtersOpen: false,
    };
  },
  computed: {
    filteredPets() {
      const maxDist = Number(this.filters.distance);
      return this.pets.filter(pet => {
        if (!pet.isOnline) return false;
        if (this.filters.breed && pet.breed !== this.filters.breed) return false;
        if (this.filters.gender && pet.gender !== this.filters.gender) return false;
        if (this.filters.age && pet.age !== this.filters.age) return false;
        if (!this.userCoords || !pet.location_coords) return true;
        try {
          const [lat, lon] = pet.location_coords.split(',').map(s => parseFloat(s.trim()));
          if (isNaN(lat) || isNaN(lon)) return true;
          return this.calculateDistance(lat, lon) <= maxDist;
        } catch { return true; }
      });
    },
    filteredMeetings() {
      return Array.isArray(this.meetings) ? this.meetings : [];
    },
    upcomingMeetings() {
      const now = new Date();
      return (Array.isArray(this.meetings) ? this.meetings : [])
        .filter(meeting => {
          if (!meeting.date || !meeting.time) return false;
          const dateStr = String(meeting.date).slice(0, 10);
          const timeStr = String(meeting.time).slice(0, 5);
          const meetingTime = new Date(`${dateStr}T${timeStr}`);
          return !isNaN(meetingTime) && meetingTime >= now;
        })
        .sort((a, b) => {
          const aDate = new Date(`${String(a.date).slice(0, 10)}T${String(a.time).slice(0, 5)}`);
          const bDate = new Date(`${String(b.date).slice(0, 10)}T${String(b.time).slice(0, 5)}`);
          return aDate - bDate;
        });
    },
  },
  methods: {
    getDistance(pet) {
      if (!pet.location_coords) return null;
      try {
        const [lat, lon] = pet.location_coords.split(',').map(s => parseFloat(s.trim()));
        if (isNaN(lat) || isNaN(lon)) return null;
        return Math.round(this.calculateDistance(lat, lon));
      } catch { return null; }
    },
    openProfile(pet) {
      this.$emit('view-pet-profile', pet);
    },
    async fetchFavourites() {
      try {
        const { data } = await axios.get(
          `${backendUrl}/api/pets/users/${this.user.id}/favourites`,
          { withCredentials: true }
        );
        this.favourites = Array.isArray(data)
          ? data.map(pet => pet.id)
          : (data.favourites || []);
      } catch (err) {
        console.error('Error fetching favourites:', err);
      }
    },
    async fetchPets() {
      try {
        const petsResponse = await fetch(`${backendUrl}/api/pets/browse`);
        const petsData = await petsResponse.json();
        this.pets = Array.isArray(petsData) ? petsData : [];
      } catch (e) {
        console.error('Failed to fetch pets', e);
      }
    },
    async fetchMeetings() {
      const { data: user } = await axios.get(`${backendUrl}/api/auth/me`, { withCredentials: true });
      if (user.user_type === 'Pet Owner') {
        await this.fetchOwnerMeetings();
      } else {
        await this.fetchLoverMeetings();
      }
    },
    async fetchOwnerMeetings() {
      const { data } = await axios.get(`${backendUrl}/api/meetings/requests`, { withCredentials: true });
      this.meetings = data;
    },
    async fetchLoverMeetings() {
      const { data } = await axios.get(`${backendUrl}/api/meetings/user`, { withCredentials: true });
      this.meetings = data;
    },
    async toggleFavourite(petId) {
      try {
        const response = await axios.patch(
          `${backendUrl}/api/pets/users/${this.user.id}/favourites`,
          { petId },
          { withCredentials: true }
        );
        this.favourites = response.data.favourites;
        localStorage.setItem('favourites', JSON.stringify(this.favourites));
      } catch (error) {
        console.error('Error toggling favourite:', error);
      }
    },
    isFavourite(petId) {
      return Array.isArray(this.favourites) && this.favourites.includes(petId);
    },
    goToPage(pageName) {
      this.sidebarOpen = false;
      this.$emit('change-page', pageName);
    },
    async initialize() {
      try {
        const userResponse = await fetch(`${backendUrl}/api/auth/check-auth`, { credentials: 'include' });
        const userData = await userResponse.json();
        this.userId = userData.id;

        const { data: me } = await axios.get(`${backendUrl}/api/auth/me`, { withCredentials: true });
        this.userType = me.user_type;

        await this.fetchPets();
        await this.fetchMyPets();
        await this.fetchBreedOptions();
        await this.fetchMeetings();
        await this.fetchFavourites();
      } catch (e) {
        console.error('Initialization error:', e);
      }
      setInterval(this.load, 30000);
    },
    async fetchMyPets() {
      const { data } = await axios.get(`${backendUrl}/api/pets/my-pets`, { withCredentials: true });
      this.myPets = data;
    },
    async fetchRecentPets() {
      const { data } = await axios.get(`${backendUrl}/api/pets/recent`, { withCredentials: true });
      this.recentPets = data;
    },
    async togglePetStatus(pet) {
      const newIsOnline = !pet.isOnline;
      await axios.patch(
        `${backendUrl}/api/pets/${pet.id}/visibility`,
        { isOnline: newIsOnline },
        { withCredentials: true }
      );
      pet.isOnline = newIsOnline;
    },
    goToBooking(petId) {
      this.$emit('change-page', 'BookPetAppointmentPage', { petId });
    },
    async fetchBreedOptions() {
      try {
        const { data } = await axios.get(`${backendUrl}/api/breeds/breeds`, { withCredentials: true });
        this.breedOptions = data;
      } catch (err) {
        console.error('Error fetching breeds:', err);
      }
    },
    calculateDistance(lat, lon) {
      if (!this.userCoords) return Infinity;
      const R = 6371;
      const dLat = this.deg2rad(lat - this.userCoords.lat);
      const dLon = this.deg2rad(lon - this.userCoords.lon);
      const a = Math.sin(dLat / 2) ** 2 +
                Math.cos(this.deg2rad(this.userCoords.lat)) *
                Math.cos(this.deg2rad(lat)) *
                Math.sin(dLon / 2) ** 2;
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
      return R * c;
    },
    deg2rad(d) { return d * Math.PI / 180; },
    detectLocation() {
      navigator.geolocation.getCurrentPosition(
        pos => { this.userCoords = { lat: pos.coords.latitude, lon: pos.coords.longitude }; },
        err => { console.error('Location error:', err); }
      );
    },
    async load() {
      const url = backendUrl + (this.userType === 'Pet Lover' ? '/api/meetings/user' : '/api/meetings/requests');
      try {
        const { data } = await axios.get(`${url}`, { withCredentials: true });
        this.meetings = data;
      } catch (e) {
        console.error('Failed to load meetings', e);
      }
    },
    formatDate(dateStr) {
      return new Date(dateStr).toLocaleDateString();
    },
    formatTime(timeStr) {
      let [h, m] = timeStr.split(':').map(Number);
      const ampm = h >= 12 ? 'PM' : 'AM';
      h = h % 12 || 12;
      return `${h}:${m.toString().padStart(2, '0')} ${ampm}`;
    },
    checkMobile() {
      this.windowWidth = window.innerWidth;
    },
    goToCalendar() {
      this.$emit('change-page', 'CalendarPage');
    },
    formatLocation(location) {
      if (!location) return 'Unknown location';
      return location.split(',')[0].trim();
    },
    handleResize() {
      this.isMobile = window.innerWidth <= 480;
    },
  },
  watch: {
    user(newUser) {
      if (newUser && newUser.user_type) {
        this.userType = newUser.user_type;
        this.fetchMyPets();
        this.fetchRecentPets();
      }
    }
  },
  mounted() {
    window.addEventListener('resize', this.handleResize);
    const favs = localStorage.getItem('favourites');
    if (favs) this.favourites = JSON.parse(favs);
    this.initialize();
    this.detectLocation();
    this.checkMobile();
    window.addEventListener('resize', this.checkMobile);
    window.addEventListener('resize', () => { this.windowWidth = window.innerWidth; });
  },
  activated() {
    this.fetchFavourites();
    // re-fetch pets so newly added/updated pets show up
    fetch(`${backendUrl}/api/pets/browse`)
      .then(r => r.json())
      .then(data => { this.pets = Array.isArray(data) ? data : []; });
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.handleResize);
  },
};
</script>

<style scoped>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;
}

body, html {
  margin: 0;
  padding: 0;
  min-height: 100vh;
}

.dashboard {
  width: 100%;
  max-width: 100vw;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0;
  margin-top: 0;
  background-size: cover;
  background-position: center center;
  background-repeat: no-repeat;
  background-attachment: fixed;
  height: 100vh;          /* change min-height to height */
  overflow: hidden;
}


.signup-wrapper {
  width: 100%;
  max-width: 100vw;
  padding: 0px 10px 20px;
  min-height: unset;   /* ← remove the 100vh */
  margin-top: 0;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: center;
}


/* ── Section title ── */
.section-title {
  font-size: 20px;
  font-weight: 600;
  color: #111;
  margin-bottom: 16px;
  margin-top: 10px;
  text-align: left;
  width: 100%;
  max-width: 1200px;
}

/* ── Hamburger & Sidebar ── */
.hamburger {
  position: fixed;
  top: 20px;
  left: 20px;
  font-size: 28px;
  background: none;
  border: none;
  cursor: pointer;
  z-index: 1000;
}

.sidebar {
  width: 240px;
  position: fixed;
  top: 0;
  left: -240px;
  height: 100%;
  transition: left 0.3s ease;
  z-index: 1000;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
}


/* ── Calendar Button ── */
.calendar-button-wrapper {
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 2000;
}

.calendar-button {
  width: 55px;
  height: 55px;
  border-radius: 50%;
  border: none;
  cursor: pointer;
  background: linear-gradient(135deg, #fffbe8, #fff8d3);
  color: #1e3a8a;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  transition: transform 0.2s, background 0.2s, box-shadow 0.2s;
}

.calendar-button:hover {
  transform: scale(1.1);
  background: linear-gradient(135deg, #ffe876, #fffbe8);
  box-shadow: 0 6px 16px rgba(30,58,138,0.25);
}


/* ── Filter Bar ── */
/* ── Filter Bar ── */
.filters-wrapper {
  width: 100%;
  max-width: 1150px;
  margin: 20px auto 0;
}

.filters-toggle {
  display: none;
}

.filters {
  position: sticky;
  top: 5px;
  z-index: 100;
  display: flex;
  flex-wrap: nowrap;          /* force one line */
  align-items: stretch;
  gap: 0;
  padding: 0;
  margin: 0 0 24px 0;
  width: 100%;
  background: #fffbe8;
  border: 1.5px solid #e8e8e8;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  overflow-x: auto;           /* scroll if needed */
  overflow-y: hidden;
}

.distance-chip {
  min-width: 220px;
}
.filter-chip {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 12px 18px;
  flex: 1;
  min-width: 0;
  transition: background 0.15s;
  align-items: center;
  text-align: center;
  position: relative;
  width: auto;
}

.filter-chip:hover { background: #ffe876; }

.chip-label {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #999;
}

.chip-select {
  appearance: none;
  -webkit-appearance: none;
  border: 1.5px solid #ffd710;
  background: #fff2b3;   /* same as pills */
  color: #555;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  padding: 6px 28px 6px 12px;
  min-height: 32px;
  text-align: center;
  transition: all 0.18s ease;
}

.chip-select:hover {
  background: #fffdf1;
  border-color: #ffd710;
}

.filter-sep {
  width: 1.5px;
  align-self: stretch;
  background: #ffefa1;
  margin: 8px 0;
  flex-shrink: 0;
}

.pill-row {
  gap: 6px;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
}

.pill {
  padding: 4px 12px;
  border-radius: 999px;
  border: 1.5px solid #ffd710;
  background: #fff2b3;
  color: #555;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.18s ease;
  white-space: nowrap;
  margin: 9px;
  margin-bottom: 2px;
}

.pill:hover { border-color: #ffd710; background: #fffdf1; color: #555; }

.pill.pill-active {
  background: #7c3aed;
  border-color: #7c3aed;
  color: #fff;
  box-shadow: 0 2px 8px rgba(124, 58, 237, 0.3);
}

.pill.pill-blue.pill-active {
  background: #2563eb;
  border-color: #2563eb;
  color: #fff;
  box-shadow: 0 2px 8px rgba(37, 99, 235, 0.3);
}

.pill.pill-pink.pill-active {
  background: #db2777;
  border-color: #db2777;
  color: #fff;
  box-shadow: 0 2px 8px rgba(219, 39, 119, 0.3);
}

.distance-chip { min-width: 160px; }

.distance-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 8px;
}

.distance-badge {
  font-size: 12px;
  font-weight: 700;
  background: #fff3b0;
  color: #92610a;
  padding: 2px 8px;
  border-radius: 999px;
  border: 1px solid #f6d860;
}

.distance-slider {
  -webkit-appearance: none;
  appearance: none;
  width: 100%;
  height: 4px;
  border-radius: 4px;
  background: linear-gradient(to right, #f59e0b 0%, #e5e7eb 100%);
  outline: none;
  cursor: pointer;
  margin-top: 4px;
}

.distance-slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #f59e0b;
  border: 3px solid #fff;
  box-shadow: 0 1px 6px rgba(0,0,0,0.2);
  cursor: pointer;
  transition: transform 0.15s;
}

.distance-slider::-webkit-slider-thumb:hover { transform: scale(1.2); }

.distance-slider::-moz-range-thumb {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #f59e0b;
  border: 3px solid #fff;
  box-shadow: 0 1px 6px rgba(0,0,0,0.2);
  cursor: pointer;
}

/* ── Pet Grid ── */
.pet-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 20px;
  padding: 4px 0 28px;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  box-sizing: border-box;
  overflow-y: auto;
  max-height: calc(100vh - 300px); 
  width: 95%;
}

/* ── Pet Card ── */
.pet-card {
  background: #ffffff;
  border-radius: 24px;
  border: 5px solid #ffffff !important;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);
  transition: transform 0.25s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.25s ease;
  cursor: default;
  max-height: 500px; 
}

.pet-card:hover {
  transform: translateY(-6px) scale(1.01);
  box-shadow: 0 16px 36px rgba(0, 0, 0, 0.11);
}

.pet-card-img-wrap {
  position: relative;
  width: 100%;
  height: 175px;
  background: #f7f3ee;
  overflow: hidden;
}

.pet-card-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  transition: transform 0.4s ease;
}

.pet-card:hover .pet-card-img { transform: scale(1.06); }

.online-dot {
  position: absolute;
  top: 12px;
  left: 13px;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #22c55e;
  border: 2.5px solid #ffffff;
  box-shadow: 0 0 0 3px rgba(34, 197, 94, 0.2);
}

.fav-btn {
  position: absolute;
  top: 10px;
  right: 11px;
  background: rgba(255, 255, 255, 0.92);
  backdrop-filter: blur(6px);
  border: none;
  border-radius: 50%;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 16px;
  transition: transform 0.2s cubic-bezier(0.34, 1.56, 0.64, 1), background 0.2s;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.10);
  line-height: 1;
}

.fav-btn:hover { transform: scale(1.2); background: rgba(255, 255, 255, 1); }
.fav-btn.fav-active { background: #fff0f3; }

.pet-body {
  padding: 15px 16px 17px;
  display: flex;
  flex-direction: column;
  gap: 5px;
  flex: 1;
}

.pet-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.pet-name {
  font-size: 16px;
  font-weight: 800;
  color: #111;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  letter-spacing: -0.2px;
}

.pet-breed, .pet-location, .pet-distance {
  font-size: 12px;
  color: #aaa;
  margin: 0;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.welcome-banner {
  width: 100vw;
  margin-left: -10px;
  margin-right: -10px;
  box-sizing: border-box;
  min-height: 134px;
  margin-bottom: 0;
  z-index: 100;
}

.badges { display: flex; gap: 5px; flex-wrap: wrap; margin-top: 4px; }

.badge {
  font-size: 11px;
  font-weight: 700;
  padding: 7px 10px;
  border-radius: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  letter-spacing: 0.01em;
}

.badge-age    { background: #f3eeff; color: #6d28d9; border: 1px solid #dccffc; }
.badge-male   { background: #eff6ff; color: #1d4ed8; border: 1px solid #d1e5ff; }
.badge-female { background: #fdf2f8; color: #be185d; border: 1px solid #ffcce8; }

.badge-vax {
  background: #f0fff4;
  border: 1px solid #bbf7d0;
  padding: 3px 7px;
  display: inline-flex;
  align-items: center;
}

.vax-icon {
  height: 18px;   /* was 28px */
  width: auto;
  display: block;
}

.view-btn {
  margin-top: auto;
  padding: 10px 14px;
  background: #eac7ff;
  color: black;
  border: none;
  border-radius: 14px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  width: 100%;
  transition: background 0.18s, transform 0.1s;
  letter-spacing: 0.02em;
  margin-top: 10px;
}

.view-btn:hover  { background: #ba71ff; }
.view-btn:active { transform: scale(0.97); }

/* ── Empty state ── */
.empty-state {
  grid-column: 1 / -1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  gap: 8px;
}

.empty-icon { font-size: 40px; }
.empty-text { font-size: 16px; font-weight: 700; color: #333; margin: 0; }
.empty-sub  { font-size: 13px; color: #bbb; margin: 0; }

/* ── Responsive ── */
@media (max-width: 700px) {
  .pet-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
  }
  .pet-card-img-wrap { height: 135px; }
  .pet-name  { font-size: 14px; }
  .pet-breed { font-size: 12px; }
  .pet-location { font-size: 12px;}
}

@media (max-width: 700px) {
  .filters {
    display: flex;
    flex-wrap: nowrap !important;   /* ← force it */
    overflow-x: auto;
    overflow-y: hidden;
    gap: 10px;
    padding: 10px;
    scroll-snap-type: x mandatory;
    margin-top: 0;
  }

  .filter-chip {
    flex: 0 0 auto;
    min-width: 140px;
    width: auto;
    scroll-snap-align: start;
  }

  .filter-sep { display: none; }
  .pet-card-img-wrap { height: 130px; }
  .pet-name { font-size: 14px; }
  .pet-breed { font-size: 12px; }
  .pet-location { font-size: 12px;}

  .filter-chip .pill-row {
    display: grid;
    grid-template-columns: repeat(2, auto);
    justify-content: center;
    gap: 6px;
  }

  .filter-chip .pill-row .pill:nth-child(3) {
    grid-column: 1 / span 2;
    justify-self: center;
  }

  .signup-wrapper {
    max-width: 700px;
    margin: 0 auto;
  }
  .pet-card { width: 250px; margin: 15px;}
}

@media (max-width: 600px) {
  .hamburger { font-size: 24px; }
}

@media (max-width: 480px) {
  .dashboard {
    background-size: cover;
    background-position: top center;
    min-height: 100dvh;
  }

  .welcome-banner {
    margin-bottom: 0px;
    min-height: 100px;  /* adjust this to match how tall the banner area is in your phone image */
    padding: 16px 20px;
  }
  .welcome-text {
    margin-bottom: 0px;
    padding: 12px 25px;
    color: #400b85;
  }
  .welcome-content {
    margin-top: 7px;
    color: #a478df;
    font-size: 12px;
  }

  .signup-wrapper {
    max-width: 100%;
    padding: 0 30px 20px;
    align-items: stretch;   /* ← add this */
  }
}

@media (max-width: 480px) {

  .filters {
    margin-top: 0; /* let the image show through */
    border: none;
    box-shadow: none;
  }

}

@media (max-width: 480px) {
  .pet-grid {
    grid-template-columns: 1fr;
    gap: 10px;
    padding: 4px 0 16px;
    max-height: calc(100vh - 10px);
    width: 100%;         /* ← add this */
  }

  .pet-card {
    flex-direction: row;
    align-items: center;
    border-radius: 16px;
    gap: 12px;
    width: 100%;
    margin-left: 0px;
  }

  .pet-card-img-wrap {
    width: 90px;
    flex-shrink: 0;
    overflow: hidden;
  }

  .online-dot {
    top: 6px;
    left: 6px;
    width: 8px;
    height: 8px;
  }

  .fav-btn {
    top: 4px;
    right: 4px;
    width: 26px;
    height: 26px;
    font-size: 12px;
  }

  .pet-body {
    padding: 0;
    gap: 4px;
    flex: 1;
    max-width: calc(100% - 90px - 25px); /* image width + gap */
  }

  .pet-name { font-size: 14px; }
  .pet-breed { font-size: 11px; }
  .pet-location { font-size: 11px;}

  .badge {
    font-size: 9px;
    padding: 2px 6px;
  }

  .view-btn {
    font-size: 11px;
    padding: 6px 10px;
    border-radius: 10px;
    margin-top: 4px;
  }
}

.filters-wrapper {
  width: 100%;
  max-width: 1150px;
  margin: 35px auto 24px;
}

.filters-toggle {
  display: none; /* hidden on desktop */
}

@media (max-width: 480px) {
  .filters-wrapper {
    width: 100vw;
    position: relative;
    left: 50%;
    transform: translateX(-50%);
    margin: 0 0 5px 0;
    border-radius: 0;
  }

  .filters-toggle {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    padding: 12px 16px;
    background: #fffbe8;
    border: 1.5px solid #e8e8e8;
    font-size: 14px;
    font-weight: 700;
    color: #4a1070;
    cursor: pointer;
    box-shadow: 0 2px 8px rgba(0,0,0,0.06);
    font-family: inherit;
    margin-bottom: 6px;
    margin-top: -5px;
  }

  .filters {
    margin: 0 !important;
    border-radius: 14px;
    border: 1.5px solid #e8e8e8 !important;
    box-shadow: 0 2px 8px rgba(0,0,0,0.06) !important;
    flex-direction: column !important;
    flex-wrap: wrap !important;
    overflow: visible !important;
    padding: 8px 0 !important;
    position: static !important;
    max-height: 1200px;
  }

  .filter-chip {
    flex: unset !important;
    width: 100% !important;
    min-width: unset !important;
    border-bottom: 1px solid #f5f0d8;
    padding: 12px 16px !important;
    align-items: flex-start !important;
    text-align: left !important;
  }

  .filter-chip:last-child { border-bottom: none; }
  .filter-sep { display: none; }

  .filters-hidden {
    display: none !important;
  }

    .pill-row {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
    width: 100%;
  }

  .pill-row .pill {
    width: 78px !important;   /* same width for all */
    min-width: 78px !important;
    max-width: 78px !important;
    padding: 7px 0 !important;
    text-align: center;
    justify-content: center;
    font-size: 11px;
  }

  /* favourites button same size too */
  .filter-chip .pill-row .pill {
    border-radius: 999px;
  }
}

</style>